/**
 * API 키 자가발급 — /?page=api-keys (로그인 필요)
 * 발급 응답의 평문 키는 이 화면에서 1회만 표시(서버는 해시만 보관).
 */
import React, { useEffect, useState, useCallback } from "react";
import ApiPortalLayout, { } from "./ApiPortalLayout";

const S = {
  page: { maxWidth: 760, margin: "0 auto", color: "#1f2937" }, // 셸(ApiPortalLayout)이 프레임 제공
  h1: { fontSize: 26, fontWeight: 800, marginBottom: 8 },
  p: { fontSize: 14, color: "#4b5563" },
  card: { border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, margin: "14px 0" },
  input: { border: "1px solid #d1d5db", borderRadius: 8, padding: "9px 12px", fontSize: 14, width: 220 },
  btn: { background: "#111827", color: "#fff", border: "none", borderRadius: 8,
    padding: "9px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer", marginLeft: 8 },
  btnDanger: { background: "#fff", color: "#b91c1c", border: "1px solid #fecaca",
    borderRadius: 6, padding: "4px 10px", fontSize: 12.5, cursor: "pointer" },
  keyBox: { background: "#0f172a", color: "#a7f3d0", padding: "12px 14px", borderRadius: 8,
    fontFamily: "ui-monospace,monospace", fontSize: 13, wordBreak: "break-all", margin: "10px 0" },
  warn: { background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e",
    padding: "8px 12px", borderRadius: 6, fontSize: 13 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13.5 },
  th: { textAlign: "left", padding: "8px 10px", borderBottom: "2px solid #e5e7eb" },
  td: { padding: "8px 10px", borderBottom: "1px solid #f3f4f6" },
};

export default function ApiKeysPage({ API_BASE_URL, onNavigate }) {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  // 로그인으로 나가면(헤더/본문 어느 버튼이든) 로그인 후 이 페이지로 복귀하도록 의도 저장
  const navIntent = (page) => {
    if (page === "login") sessionStorage.setItem("apikeys_after_login", "1");
    onNavigate(page);
  };
  const [keys, setKeys] = useState(null);
  const [name, setName] = useState("");
  const [newKey, setNewKey] = useState(null); // {key, name} 평문 1회
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const authHeaders = useCallback(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/keys`, { headers: authHeaders() });
      const j = await r.json();
      if (j.success) setKeys(j.data);
      else setErr(j.message || "목록을 불러오지 못했습니다.");
    } catch (e) { setErr(e.message); }
  }, [API_BASE_URL, authHeaders]);

  useEffect(() => { if (token) load(); }, [token, load]);

  if (!token) {
    return (
      <ApiPortalLayout active="keys" onNavigate={navIntent}>
        <div style={S.page}>
          <h1 style={S.h1}>API 키 발급</h1>
          <p style={S.p}>API 키를 발급하려면 먼저 로그인하세요.</p>
          <button style={S.btn} onClick={() => navIntent("login")}>로그인 / 시작하기</button>
        </div>
      </ApiPortalLayout>
    );
  }

  const issue = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/keys`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ name: name.trim() || "default" }),
      });
      const j = await r.json();
      if (j.success) { setNewKey(j.data); setName(""); load(); }
      else setErr(j.message || "발급 실패");
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const revoke = async (id) => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/keys/${id}`, { method: "DELETE", headers: authHeaders() });
      const j = await r.json().catch(() => null);
      if (!r.ok || !(j && j.success)) {
        setErr((j && j.message) || `폐기 실패 (HTTP ${r.status})`);
        return;
      }
      setConfirmId(null); load();
    } catch (e) { setErr(e.message); }
  };

  return (
    <ApiPortalLayout active="keys" onNavigate={navIntent}>
    <div style={S.page}>
      <h1 style={S.h1}>API 키</h1>
      <p style={S.p}>
        loginHS 세번분류 API v1 호출용 키를 발급·관리합니다 (무료 베타).{" "}
        <a href="/?page=api-docs" onClick={(e) => { e.preventDefault(); onNavigate("api-docs"); }}>API 문서 보기</a>
      </p>

      <div style={S.card}>
        <input style={S.input} placeholder="키 이름 (예: 통관솔루션 연동)"
          value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
        <button style={S.btn} disabled={busy} onClick={issue}>새 키 발급</button>
        {newKey && (
          <div>
            <div style={S.keyBox}>{newKey.key}</div>
            <div style={S.warn}>
              ⚠ 이 키는 지금 이후 <b>다시 볼 수 없습니다</b> — 안전한 곳에 복사해 두세요.
              <button style={{ ...S.btnDanger, marginLeft: 10, color: "#065f46", borderColor: "#a7f3d0" }}
                onClick={() => navigator.clipboard && navigator.clipboard.writeText(newKey.key)}>복사</button>
              <button style={{ ...S.btnDanger, marginLeft: 6, color: "#374151", borderColor: "#d1d5db" }}
                onClick={() => setNewKey(null)}>닫기</button>
            </div>
          </div>
        )}
        {err && <div style={{ ...S.warn, background: "#fef2f2", borderColor: "#fecaca", color: "#b91c1c", marginTop: 8 }}>{err}</div>}
      </div>

      <div style={S.card}>
        {keys === null ? <p style={S.p}>불러오는 중…</p>
          : keys.length === 0 ? <p style={S.p}>발급된 키가 없습니다.</p>
          : (
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>키</th><th style={S.th}>이름</th><th style={S.th}>생성일</th>
              <th style={S.th}>30일 사용량</th><th style={S.th}>상태</th><th style={S.th}></th>
            </tr></thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} style={k.revoked_at ? { opacity: 0.45 } : undefined}>
                  <td style={S.td}><code>{k.key_prefix}…</code></td>
                  <td style={S.td}>{k.name}</td>
                  <td style={S.td}>{String(k.created_at).slice(0, 10)}</td>
                  <td style={S.td}>{k.items_30d} items</td>
                  <td style={S.td}>{k.revoked_at ? "폐기됨" : "활성"}</td>
                  <td style={S.td}>
                    {!k.revoked_at && (confirmId === k.id ? (
                      <>
                        <button style={S.btnDanger} onClick={() => revoke(k.id)}>정말 폐기</button>
                        <button style={{ ...S.btnDanger, color: "#374151", borderColor: "#d1d5db", marginLeft: 6 }}
                          onClick={() => setConfirmId(null)}>취소</button>
                      </>
                    ) : (
                      <button style={S.btnDanger} onClick={() => setConfirmId(k.id)}>폐기</button>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
    </ApiPortalLayout>
  );
}
